import React from "react";
import { FormattedMessage } from "react-intl";
import driversEntityOptions from "../../drivers/config/entity";
import trucksEntityOptions from "../../trucks/config/entity";
import supplierContractsOptions from "../../supplierContracts/config/entity";
import contractsEntityOptions from "../../contracts/config/entity";

/*
{
    // Title and subtitle values which shown next to avatar
    id?: string, 'title' | 'subtitle'

    // Attribute key to show and update the corresponding entity field
    name: string,

    // section label if it's a details field
    label: string,
    
    // Allow field editing
    isEditable?: bool, 
    
    // AbstractInput type for editable fields or relation 
    // Using type = 'relation' will show this entity as table in profileEntities
    type: string,  // Ex:'relation' | 'string' | 'dateAndTime' | 'list' | 'number' | 'select' | 'checkbox' 

    // Entity options to be used in in profileEntities table
    // (Required if type = 'relation')
    entityOptions: [entityOption], //Ex: trucksEntityOptions

    // Url to fetch the field entity list 
    // (Required if type = 'list' && isEditable = true)
    url: string, // Ex:'/cities'

    // Attribute key to show the field entity value if it's an included entity
    // and used in AbstractDropdown to show list item value
    filterValue: string, // Ex:'nameAr'

    // Field entity list if type = 'select'
    // (Required if type = 'select' && isEditable = true)
    data: [
        { value: string, text: string },
        // Ex: { value: 'on_demand', text: 'On Demand' }
    ],

    // Entity name to get accessed from included entities
    // (Required if type = 'relation')
    entityName: string, // Ex:'supplierContracts'

    // If included entity is different from entityName 
    // (required if relation is one to one between EntityProfile and includedEntity)
    includeEntityName?: string, // Ex:'supplierContract'

    // Update the entity if it's an included entity field
    updateEntityOnChange?: boolean,
        
    // Show value formatted using lodash startCase
    // Example: 'on_demand' will be formatted to 'On Demand'
    format?: boolean,

    // String to be shown if field input value isn't valid
    errorMessage?: string, // Ex: 'Name must be in English or Arabic letters with minimum length of 3'

    // Specify field input validation criteria
    validation?: {
        pattern: /^[\u0621-\u064A\u0660-\u0669a-zA-Z ]+$/,
        required: true,
    },

    // Groups profile details
    group: string; ex:'contractDetails' used as section title
}
 */

export const SUPPLIER_GROUP_FIELDS = {
    CONTRACT: {
        id: "contract",
        label: "Contract",
        options: supplierContractsOptions,
        relationKey: "supplierContractId",
    },
};

export default [
    {
        name: "id",
        label: "Supplier ID",
    },
    {
        id: "subtitle",
        name: "companyName",
        label: "Supplier Company Name",
        type: "string",
        isEditable: true,
    },
    {
        id: "title",
        name: "name",
        label: "Name",
        type: "string",
        isEditable: true,
        errorMessage: "Name must be in English or Arabic letters with minimum length of 3",
        validation: {
            pattern: /^[\u0621-\u064A\u0660-\u0669a-zA-Z ]+$/,
            required: true,
        },
    },
    {
        name: "phoneNumber",
        label: "Phone Number",
        type: "string",
        isEditable: true,
        errorMessage: "Phone Number is required and must be 11 digits starting with 0",
        validation: {
            pattern: /^0\d{10}$/,
            required: true,
        },
    },
    {
        name: "nationalId",
        label: "National Id Number",
        type: "string",
        isEditable: true,
        errorMessage: "National Id must be number",
        validation: {
            pattern: /^[0-9]+$/,
            required: true,
        },
    },

    {
        name: "cityId",
        label: "City",
        type: "list",
        isEditable: true,
        url: "/cities",
        filterValue: "nameAr",
        entityName: "cities",
        includeEntityName: "city",
    },
    {
        name: "governorate",
        label: "Governorate",
        type: "string",
    },
    {
        name: "commercialRegister",
        label: "Commercial Register",
        type: "string",
        isEditable: true,
    },
    {
        name: "taxCard",
        label: "Tax Card",
        type: "string",
        isEditable: true,
    },
    {
        name: "bankAccountNumber",
        label: "Bank Account Number",
        type: "string",
        isEditable: true,
    },
    {
        name: "attachments",
        label: "Attachment",
        type: "attachment",
        isEditable: true,
        validation: {
            required: false,
        },
        entityName: "attachments",
        includeEntityName: "attachments",
    },
    {
        name: "active",
        label: "Active",
        type: "checkbox",
    },
    {
        name: "contracted",
        label: "Contracted",
        type: "checkbox",
        entityName: "supplierContracts",
        includeEntityName: "supplierContract",
        group: SUPPLIER_GROUP_FIELDS.CONTRACT,
        isEditable: true,
    },
    {
        name: "signDate",
        label: "Contract Sign Date",
        type: "dateAndTime",
        entityName: "supplierContracts",
        includeEntityName: "supplierContract",
        group: SUPPLIER_GROUP_FIELDS.CONTRACT,
        isEditable: true,
    },
    {
        name: "endDate",
        label: "Contract End Date",
        type: "dateAndTime",
        entityName: "supplierContracts",
        includeEntityName: "supplierContract",
        group: SUPPLIER_GROUP_FIELDS.CONTRACT,
        isEditable: true,
    },
    {
        name: "operationModule",
        label: "Operation Module",
        entityName: "supplierContracts",
        includeEntityName: "supplierContract",
        group: SUPPLIER_GROUP_FIELDS.CONTRACT,
        isEditable: true,
        updateEntityOnChange: true,
        type: "select",
        format: true,
        data: [
            // TODO: Move to contract entity constants after adding contract table
            { value: "monthly", text: "Monthly" },
            { value: "on_demand", text: "On Demand" },
            { value: "monthly_and_on_demand", text: "Monthly and on demand" },
        ],
    },
    {
        name: "contractDuration",
        label: "Contract Duration",
        type: "duration",
        durationFields: ["signDate", "endDate"],
        entityName: "supplierContracts",
        includeEntityName: "supplierContract",
        group: SUPPLIER_GROUP_FIELDS.CONTRACT,
    },
    {
        name: "truckIds",
        label: "Trucks",
        type: "relation",
        entityName: "trucks",
        entityOptions: trucksEntityOptions,
    },
    {
        name: "driverIds",
        label: "Drivers",
        type: "relation",
        entityName: "drivers",
        entityOptions: driversEntityOptions,
    },
    {
        name: "trips",
        label: "Trips",
        type: "relation",
        entityName: "trips",
    },
    {
        name: "contracts",
        label: <FormattedMessage id="driver.contracts" />,
        type: "relation",
        entityName: "contracts",
        entityOptions: contractsEntityOptions,
    },
];
